import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faSearchengin } from '@fortawesome/free-brands-svg-icons'
import {
  faCog,
  faLock,
  faPaintBrush,
  faTableCells,
  faTags,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BadgeIcon,
  Button,
  Input,
  LoadingBlock,
  Table,
  TableColumnHeader,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useHistory } from 'react-router-dom'

import { msalInstance } from '../../auth/config'
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal/DeleteConfirmationModal'
import FiltersModal from '../../components/FiltersModal/FiltersModal'
import { SortButton } from '../../components/SortButton/SortButton'
import { useDispatch, useStore } from '../../store/store'
import { Project } from '../../store/store.types'
import {
  deleteProject,
  emptyProjects,
  fetchProjects,
  setProject,
} from '../../store/storeActions'
import { Sorting, SortingDirection } from '../../types/sorting'
import { formatDate } from '../../utils/date'
import { locations } from '../../utils/locations'
import { serializeSorting } from '../../utils/sorting'
import ProjectsCreateUpdate from './ProjectsCreateUpdate'
import useStyles from './ProjectsList.styles'

const itemsPerPage = 50

interface ProjectColumns extends Project {
  actions?: string
}

type ProjectFilters = {
  name?: string
  description?: string
}

const ProjectsList = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()
  const { projects } = useStore()
  const dispatch = useDispatch()
  const [isDeleteSubmitting, setDeleteSubmitting] = useState(false)
  const currentUserEmail = msalInstance.getActiveAccount()?.username
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState<ProjectColumns>()
  const [projectsCreateUpdateModal, setProjectsCreateUpdateModal] = useState(
    false
  )
  const [sorting, setSorting] = useState<Sorting>({})
  const sortingRef = useRef<Sorting>({})
  const [projectFilters, setProjectFilters] = useState<ProjectFilters>({})
  const projectFiltersRef = useRef<ProjectFilters>({})
  const [listKey, setListKey] = useState(0)

  const getDetailsLocation = (projectId: number) =>
    locations.products(projectId)

  const serializeFilters = (f: ProjectFilters): string => {
    const parts: string[] = []
    if (f.name) parts.push(`name=${encodeURIComponent(f.name)}`)
    if (f.description)
      parts.push(`description=${encodeURIComponent(f.description)}`)
    return parts.length ? `&${parts.join('&')}` : ''
  }

  const resetList = () => {
    emptyProjects(dispatch)
    setListKey((k) => k + 1)
  }

  const handleLoadMore = (page: number) => {
    const orderingParam = serializeSorting(sortingRef.current) || undefined
    const filterParam = serializeFilters(projectFiltersRef.current) || undefined
    fetchProjects(
      dispatch,
      page !== 0,
      itemsPerPage,
      page * itemsPerPage,
      orderingParam,
      filterParam
    )
  }

  const handleSort = (key: string, direction: SortingDirection) => {
    const newSorting = { ...sortingRef.current, [key]: direction }
    sortingRef.current = newSorting
    setSorting(newSorting)
    resetList()
  }

  const handleSaveFilters = (f: Partial<ProjectFilters>) => {
    projectFiltersRef.current = f as ProjectFilters
    setProjectFilters(f as ProjectFilters)
    resetList()
  }

  const handleRemoveFilters = () => {
    projectFiltersRef.current = {}
    setProjectFilters({})
    resetList()
  }

  const filterLabels: { [key in keyof ProjectFilters]: string } = {
    name: t('Name'),
    description: t('Description'),
  }

  const renderProductCounters = (
    productCounters: Project['productCounters']
  ) => (
    <>
      {!!productCounters.anomaly && (
        <BadgeIcon
          icon={<FontAwesomeIcon icon={faSearchengin as IconProp} size="lg" />}
          badge={productCounters.anomaly.toString()}
          size="xs"
          title={t('Number of {{ type }} products included in the project', {
            type: 'anomaly detection',
          })}
        />
      )}
      {!!productCounters.classification && (
        <BadgeIcon
          icon={<FontAwesomeIcon icon={faTags} />}
          badge={productCounters.classification.toString()}
          size="xs"
          title={t('Number of {{ type }} products included in the project', {
            type: t('classification'),
          })}
        />
      )}
      {!!productCounters.segmentation && (
        <BadgeIcon
          icon={<FontAwesomeIcon icon={faPaintBrush} />}
          badge={productCounters.segmentation.toString()}
          size="xs"
          title={t('Number of {{ type }} products included in the project', {
            type: t('segmentation'),
          })}
        />
      )}
      {!!productCounters.classificationPatch && (
        <BadgeIcon
          icon={<FontAwesomeIcon icon={faTableCells} />}
          badge={productCounters.classificationPatch.toString()}
          size="xs"
          title={t('Number of {{ type }} products included in the project', {
            type: t('classification patch'),
          })}
        />
      )}
    </>
  )

  const columns: TableColumnInstance<ProjectColumns, keyof ProjectColumns>[] = [
    {
      id: 'private',
      header: '',
      th: true,
      width: 1,
      template: useCallback(
        (p) => (p.private && <FontAwesomeIcon icon={faLock} />) || null,
        []
      ),
    },
    {
      id: 'name',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Name')}
            sorting={
              <SortButton
                sort={sorting?.name}
                onSort={(direction) => handleSort('name', direction)}
              />
            }
          />
        </div>
      ),
      th: true,
    },
    {
      id: 'description',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Description')}
            sorting={
              <SortButton
                sort={sorting?.description}
                onSort={(direction) => handleSort('description', direction)}
              />
            }
          />
        </div>
      ),
    },
    { id: 'createdBy', header: t('Created by') },
    {
      id: 'creationDate',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Created on')}
            sorting={
              <SortButton
                sort={sorting?.creationDate}
                onSort={(direction) => handleSort('creationDate', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ creationDate }) => formatDate(creationDate),
    },
    {
      id: 'productCounters',
      header: t('Products'),
      template: ({ productCounters }) => renderProductCounters(productCounters),
    },
    {
      id: 'actions',
      header: t('Actions'),
      noWrap: true,
      align: 'right',
      width: 0,
      template: useCallback(
        (p) => (
          <>
            <Button
              data-testid="editProjectButton"
              onClick={(e) => {
                e.stopPropagation()
                setSelectedProject(p)
                setProjectsCreateUpdateModal(true)
              }}
              label={t('Edit')}
              hideLabel
              icon={<FontAwesomeIcon icon={faCog} />}
              buttonType="button"
              type="blank-default"
            />

            <Button
              data-testid="deleteProjectButton"
              onClick={(e) => {
                e.stopPropagation()
                setSelectedProject(p)
                setDeleteConfirmationModal(true)
              }}
              label={t('Delete')}
              hideLabel
              icon={<FontAwesomeIcon icon={faTrash} />}
              disabled={isDeleteSubmitting}
              buttonType="button"
              type="blank-destroy"
              size="sm"
            />
          </>
        ),
        [isDeleteSubmitting, t]
      ),
    },
  ]

  return (
    <div id="divProjectsListId">
      <div className={classes.header}>
        <FiltersModal<ProjectFilters>
          initialValues={projectFilters}
          labels={filterLabels}
          removeDisabled
          onSave={handleSaveFilters}
          onRemove={handleRemoveFilters}
        >
          {({ filters: f, setFilters }) => (
            <>
              <Input
                name="nameFilter"
                id="nameFilter"
                type="text"
                label={t('Name')}
                placeholder={t('Filter...')}
                value={f.name || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilters((prev) => ({
                    ...prev,
                    name: e.target.value || undefined,
                  }))
                }
              />
              <Input
                name="descriptionFilter"
                id="descriptionFilter"
                type="text"
                label={t('Description')}
                placeholder={t('Filter...')}
                value={f.description || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilters((prev) => ({
                    ...prev,
                    description: e.target.value || undefined,
                  }))
                }
              />
            </>
          )}
        </FiltersModal>
      </div>
      <InfiniteScroll
        key={listKey}
        pageStart={-1}
        loadMore={handleLoadMore}
        hasMore={projects ? projects.results.length < projects.count : true}
        loader={<LoadingBlock message={t('Loading')} key="loading" />}
      >
        <Table
          columns={columns}
          data={projects ? projects.results : []}
          alternateRows
          onRowClick={(p) => {
            history.push(getDetailsLocation(p.id))
            setProject(dispatch, {
              id: p.id,
              name: p.name,
              description: p.description,
              creationDate: p.creationDate,
              private: p.private,
              createdBy: p.createdBy,
              productCounters: p.productCounters,
            })
          }}
          isEmpty={
            projects && projects.count === 0 && projects.results.length <= 0
          }
          emptyMessage={t(
            `There's nothing here. Try creating a new {{ itemName }}.`,
            { itemName: t('project') }
          )}
        />
        {projects &&
          projects.results.length >= projects.count &&
          projects.count > 0 && (
            <div className={classes.endOfList}>
              {t('All {{count}} records loaded', { count: projects.count })}
            </div>
          )}
      </InfiniteScroll>

      {deleteConfirmationModal && selectedProject && (
        <DeleteConfirmationModal
          onCloseRequest={() => setDeleteConfirmationModal(false)}
          onDeleteRequest={() =>
            deleteProject(dispatch, selectedProject.id, setDeleteSubmitting)
          }
        />
      )}

      {projectsCreateUpdateModal && selectedProject && (
        <ProjectsCreateUpdate
          setModal={setProjectsCreateUpdateModal}
          type="update"
          projectId={selectedProject.id}
          createdBy={
            selectedProject.createdBy === currentUserEmail
              ? selectedProject.createdBy
              : undefined
          }
          initialValues={{
            name: selectedProject.name,
            description: selectedProject.description,
            private: selectedProject.private,
          }}
        />
      )}
    </div>
  )
}

export default ProjectsList
